import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAuthContext } from '../../../feature/auth/context/hook/useAuthContext';
import { useAutomaticLogoutContext } from '../../../feature/security/context/hook/useAutomaticLogoutContext';
import { loginPrincipalThunk } from '../../../feature/security/ducks/principalContextActions';
import { getStudySelector, isLoggedInSelector } from '../../../feature/security/ducks/principalContextSelectors';
import { clearSelectedStudyId, studiesThunk } from '../../../feature/studies/ducks/studiesActions';
import { getStudiesSelector } from '../../../feature/studies/ducks/studiesSelectors';
import { loadStudyConfigThunk, resetStudyConfigAction } from '../../../feature/study-config/ducks/studyConfigActions';
import { getStudyConfigStateSelector } from '../../../feature/study-config/ducks/studyConfigSelectors';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { StudiesContextState } from './StudiesContextState';

export const useStudiesContextProvider = (): StudiesContextState => {
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAuthContext();
  const isLoggedInOnStudy = useSelector(isLoggedInSelector);
  const { lastSelectedStudyId } = useAutomaticLogoutContext();
  const { error, isLoading, studies, selectedStudyId, hasMultipleStudies } = useSelector(getStudiesSelector);
  const selectedStudy = useSelector(getStudySelector);
  const { config: selectedStudyConfig } = useSelector(getStudyConfigStateSelector);

  const tenantId = selectedStudyId || lastSelectedStudyId;
  const clearSelectedStudy = useCallback(() => dispatch(clearSelectedStudyId()), [dispatch]);

  useEffect(() => {
    if (isAuthenticated && tenantId && !isLoggedInOnStudy) {
      dispatch(loginPrincipalThunk({ tenantId }));
    }
  }, [dispatch, isAuthenticated, isLoggedInOnStudy, selectedStudyId, tenantId]);

  useEffect(() => {
    if (isAuthenticated && !selectedStudyId) {
      dispatch(studiesThunk());
    }
  }, [dispatch, selectedStudyId, isAuthenticated]);

  useEffect(() => {
    if (selectedStudy?.dcode) {
      dispatch(loadStudyConfigThunk());
    } else {
      dispatch(resetStudyConfigAction());
    }
  }, [dispatch, selectedStudy]);

  return useMemo(
    () => ({
      clearSelectedStudy,
      isLoadingStudies: isLoading ?? false,
      selectedStudy,
      selectedStudyConfig,
      selectedStudyId,
      studies,
      studiesError: error,
      hasMultipleStudies,
    }),
    [
      clearSelectedStudy,
      error,
      isLoading,
      selectedStudy,
      selectedStudyConfig,
      selectedStudyId,
      studies,
      hasMultipleStudies,
    ],
  );
};
